import type { TabularDataRow } from '../types';
import type { Analysis, AnalysisResponse, FactorLensWithReturns } from 'venn-api';
import { type ComparisonSubject, Dates, type ExcelCell } from 'venn-utils';
import getAnalysisBlocks from '../analysisBlocks';
import type { DateRange } from 'venn-ui-kit';
import { getAppTitle, getTextThemeProvider } from 'venn-ui-kit';
import { onExportExcel } from 'venn-components';
import times from 'lodash/times';
import moment from 'moment/moment';

const BLOCK_ROW_SPACING = 2;
const SHEET_NAME = `${getAppTitle()} Comparison`;

const getFilename = (dateRange: DateRange, analysisResponse?: AnalysisResponse) => {
  const start =
    analysisResponse?.startTime && analysisResponse?.frequency
      ? Dates.toDDMMMYYYY(moment.utc(analysisResponse.startTime).valueOf(), analysisResponse.frequency)
      : dateRange.from
        ? Dates.toYYYYMMDD(dateRange.from, '-')
        : 'start';
  const end =
    analysisResponse?.endTime && analysisResponse?.frequency
      ? Dates.toDDMMMYYYY(moment.utc(analysisResponse.endTime).valueOf(), analysisResponse.frequency)
      : dateRange.to
        ? Dates.toYYYYMMDD(dateRange.to, '-')
        : 'end';
  return `${getTextThemeProvider().downloadComparisonFileName}_${start}-${end}${analysisResponse?.frequency ? `-${analysisResponse.frequency}` : ''}`;
};

export const xlsxDownload = async (
  subjects: ComparisonSubject[],
  analyses: Analysis[],
  relative: boolean,
  factorLens: FactorLensWithReturns,
  dateRange: DateRange,
  isPivoted: boolean,
  hideForecasts: boolean,
  analysisResponse?: AnalysisResponse,
) => {
  const blocksWithData = getAnalysisBlocks(hideForecasts)
    .filter((block) => !block.hideInPrint)
    .map((block) => ({
      ...block,
      data: block?.extractor?.(
        subjects,
        analyses.find((a) => a.analysisType === block.type),
        relative,
        factorLens,
      ),
    }));

  const allExcelData: (ExcelCell | undefined)[][] = [];

  if (!isPivoted) {
    for (const block of blocksWithData) {
      const headerRow: ExcelCell[] = [
        {
          value: block.getTitle(relative),
          bold: true,
        },
        ...subjects
          .filter((s) => !(relative && s.isBenchmark))
          .map((s) => ({
            value: s.analysisSubject?.name,
            bold: true,
          })),
      ];
      allExcelData.push(headerRow);

      // Convert each row to an array of values
      block.data?.forEach((data: TabularDataRow) => {
        const cellData: ExcelCell[] = [];
        for (let subjectIdx = 0; subjectIdx < subjects.length; subjectIdx++) {
          const subject = subjects[subjectIdx]!;
          // Skip the benchmark column when relative
          if (!(relative && subject.isBenchmark)) {
            cellData.push({
              value: data?.[subjectIdx]?.value ?? '--',
              percentage: data?.type === 'percentage',
              digits: 2,
            });
          }
        }
        allExcelData.push([{ value: data.label }, ...cellData]);
      });

      times(BLOCK_ROW_SPACING, () => allExcelData.push([]));
    }
  } else {
    const EMPTY: ExcelCell = { value: undefined };

    const superHeaderRow: ExcelCell[] = [EMPTY];
    const headerRow: ExcelCell[] = [EMPTY];
    const cellData: ExcelCell[][] = [];

    subjects.forEach((s) => {
      cellData.push([
        {
          value: s.analysisSubject?.name,
          bold: true,
        },
      ]);
    });

    for (const block of blocksWithData) {
      superHeaderRow.push({
        value: block.getTitle(relative),
        bold: true,
      });

      block.data?.forEach((data: TabularDataRow, rowIdx: number) => {
        if (rowIdx !== 0) {
          superHeaderRow.push(EMPTY);
        }
        headerRow.push({ value: data.label });

        subjects.forEach((_, subjectIdx: number) => {
          cellData[subjectIdx]!.push({
            value: data?.[subjectIdx]?.value ?? '--',
            percentage: data?.type === 'percentage',
            digits: 2,
          });
        });
      });
    }

    if (relative && subjects?.[0]!.isBenchmark) {
      cellData.shift();
    }

    allExcelData.push(superHeaderRow);
    allExcelData.push(headerRow);
    cellData.forEach((row: ExcelCell[]) => {
      allExcelData.push(row);
    });
  }

  const filename = getFilename(dateRange, analysisResponse);
  await onExportExcel(allExcelData, filename, SHEET_NAME, true);
};
